import {
  type AccountDraft,
  DEFAULT_ACCOUNT_RULE_NAME,
  type JsonObject,
  offeredTiers,
  readAccount,
  writeAccount
} from './policyDocument'

interface Props {
  parameters: JsonObject
  severities: string[]
  onChange: (parameters: JsonObject) => void
}

/**
 * Which account is signed in to the assistant, and what to do about the plan it is on.
 *
 * The division here is the one the agent catalog already makes. How the account row is found — the
 * class name, the control types, the plan words, the region of the window — belongs to the assistant
 * and is not shown: those selectors were read off live windows rather than guessed, and an operator
 * has no way to improve them and every way to break them. What is shown is the two decisions that are
 * genuinely theirs: whether to read the account at all, and whether a plan is worth an alert.
 *
 * Those two are deliberately separate. A policy may record the account and raise nothing, which is
 * what an operator wants while finding out how many machines are signed in to a personal plan before
 * deciding whether that is a problem worth alerting on.
 *
 * Like every other panel here, this edits `parameters` in place rather than rebuilding it, so the
 * selectors and the region survive somebody opening the policy to change a severity.
 */
export function AccountPanel({ parameters, severities, onChange }: Props) {
  const account = readAccount(parameters)
  const set = (next: AccountDraft) => onChange(writeAccount(parameters, next))

  if (!account.available)
    return (
      <div className="hint" data-testid="account-unavailable">
        This assistant's account row has not been read yet, so there is nothing to capture. Claude in a
        browser, ChatGPT in a browser, and the Claude desktop app can report the signed-in account and
        its plan.
      </div>
    )

  const alert = account.alert
  const tiers = offeredTiers(account)

  const toggleTier = (tier: string, on: boolean) => {
    if (!alert) return
    set({
      ...account,
      alert: {
        ...alert,
        onTiers: on ? [...alert.onTiers, tier] : alert.onTiers.filter((entry) => entry !== tier)
      }
    })
  }

  return (
    <div data-testid="account-panel">
      <label className="inline-check">
        <input
          type="checkbox"
          checked={account.captureTier}
          onChange={(event) =>
            set({
              ...account,
              captureTier: event.target.checked,
              // An alert compares the plan, so one left behind by switching the plan off would be a
              // document the server refuses to save, citing a field no longer on the screen.
              alert: event.target.checked ? account.alert : null
            })
          }
          data-testid="account-capture-tier"
        />
        <span>Record the subscription plan</span>
      </label>

      <label className="inline-check">
        <input
          type="checkbox"
          checked={account.captureDisplayName}
          onChange={(event) => set({ ...account, captureDisplayName: event.target.checked })}
          data-testid="account-capture-name"
        />
        <span>Record the signed-in account name</span>
      </label>
      <div className="hint">
        The name is read from the assistant's own account row and travels with the enforcement event,
        so it is personal data leaving the machine. Record it when an investigator will need to know
        whose account it was, and leave it off when the plan alone answers the question.
      </div>

      {account.captureTier && (
        <>
          <label className="inline-check">
            <input
              type="checkbox"
              checked={alert !== null}
              onChange={(event) =>
                set({
                  ...account,
                  alert: event.target.checked
                    ? {
                        ruleName: DEFAULT_ACCOUNT_RULE_NAME,
                        severity: 'high',
                        onTiers: [],
                        requireContentMatch: false
                      }
                    : null
                })
              }
              data-testid="account-alert-enabled"
            />
            <span>Raise an alert on some of these plans</span>
          </label>
          <div className="hint">Left off, the plan is recorded and nothing is raised.</div>
        </>
      )}

      {account.captureTier && alert && (
        <div className="subsection" data-testid="account-alert">
          <div className="field">
            <label>Plans worth an alert</label>
            <div className="check-row">
              {tiers.map((tier) => (
                <label className="inline-check" key={tier}>
                  <input
                    type="checkbox"
                    checked={alert.onTiers.includes(tier)}
                    onChange={(event) => toggleTier(tier, event.target.checked)}
                    data-testid={`account-tier-${tier}`}
                  />
                  <span>{tier}</span>
                </label>
              ))}
            </div>
            <div className="hint">
              The personal plans are usually the ones that matter. A corporate plan is what this is
              distinguishing them from, so alerting on it makes the alert mean nothing.
            </div>
          </div>

          <label className="inline-check">
            <input
              type="checkbox"
              checked={alert.requireContentMatch}
              onChange={(event) =>
                set({ ...account, alert: { ...alert, requireContentMatch: event.target.checked } })
              }
              data-testid="account-require-content"
            />
            <span>Only when a dictionary term is in the prompt as well</span>
          </label>
          <div className="hint">
            The narrower question: not who is on a personal plan, but who is putting company data into
            one.
          </div>

          <div className="field">
            <label htmlFor="account-severity">Severity</label>
            <select
              id="account-severity"
              value={alert.severity}
              onChange={(event) => set({ ...account, alert: { ...alert, severity: event.target.value } })}
              data-testid="account-severity"
            >
              {severities.map((severity) => (
                <option key={severity} value={severity}>
                  {severity}
                </option>
              ))}
            </select>
            <div className="hint">
              This has to reach the minimum severity under "When a rule matches", or the plan is detected,
              recorded, and never reported. The findings on this page say so when it does not.
            </div>
          </div>

          <details className="subsection">
            <summary>What the alert is called</summary>
            <div className="field">
              <label htmlFor="account-rule-name">Rule name</label>
              <input
                id="account-rule-name"
                value={alert.ruleName}
                onChange={(event) => set({ ...account, alert: { ...alert, ruleName: event.target.value } })}
                data-testid="account-rule-name"
              />
              <div className="hint">
                What an analyst reads in the alert queue, and the name this reports under. No bound
                dictionary may use it: the agent requires rule names to be unique across this rule and
                the detection rules together, so publishing is refused when one does.
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  )
}
